import React from "react";
import PropTypes from "prop-types";
import styles from "./index.module.scss";
import SocialComponent from "@/components/SocialComponent";
import { Box } from "@kohler-dsm-library/kohler-core";
import Link from "next/link";
import { renderRichText, applyLocaleToUrl } from "@/utils/formatters";

import { useRouter } from "next/router";

const ArticleImageIcon = ({ articleImageIconProps = {} }) => {
  const { locale } = useRouter();

  const {
    mod = "white",
    image = {},
    heading = "",
    headingFontSize,
    description = "",
    richDescription = "",
    textPosition = "",
    showVideo = false,
    videoUrl = "",
    showPrintAndShareIcon = true,
    cta = {},
    showContentAboveImage = false,
    showContentBelowImage = true,
    showLine = true,
    bottomSpacer = false,
    topSpacer = false,
    showTopLine = false,
    removeBelowMargin = false,
    headingColor = "",
    showLineColor = "",
  } = articleImageIconProps || {};

  const selectedLocale = locale == "en" ? true : false;
  const description2 = renderRichText(richDescription, locale) || "";

  const { label = "", url = "", mediaUrl = "" } = cta?.fields || {};
  const {
    imageUrl = "",
    altText = "",
    asset = {},
    externalUrl = "",
  } = image?.fields || {};
  const { url: assetUrl = "" } = asset?.fields?.file || {};

  let bottomSpacerClass = bottomSpacer
    ? "koh-article-pages-container-without-bottomSpace"
    : "koh-article-pages-container";
  let topSpacerClass = topSpacer
    ? "belowContainer-without-topSpace"
    : "belowContainer";
  let topMarginClass = topSpacer
    ? "c-koh-article-pages-margin10"
    : "c-koh-article-pages-margin25";
  let showTopLineClass = showTopLine ? "top-line" : "";
  let removeBelowMarginClass = removeBelowMargin ? "remove-below-margin" : "";

  const colorMap = {
    Blue: "#92d7e9",
    White: "#ffffff",
    Black: "#000000",
    Yellow: "#f2a054",
    Orange: "#b37700",
  };
  const isBlackBackground = mod;
  let finalHeadingColor;

  if (isBlackBackground) {
    if (headingColor && colorMap[headingColor]) {
      finalHeadingColor = colorMap[headingColor];
    } else {
      finalHeadingColor = "#ffffff";
    }
  } else {
    finalHeadingColor = "#000000";
  }

  if (isBlackBackground && headingColor && colorMap[headingColor]) {
    finalHeadingColor = colorMap[headingColor];
  } else if (isBlackBackground === "black") {
    finalHeadingColor = "#ffffff";
  } else {
    finalHeadingColor = "#000000";
  }
  let finalLineColor;
  if (isBlackBackground) {
    if (showLineColor && colorMap[showLineColor]) {
      finalLineColor = colorMap[showLineColor];
    } else {
      finalLineColor = "#ffffff";
    }
  }

  return (
    <Box className={styles["c-koh-article-pages"]} mod={mod}>
      <div
        className={`print-hide-row ${topMarginClass} ${showTopLineClass} ${removeBelowMarginClass} ${
          showContentBelowImage
            ? `${topSpacerClass} ${showLine}`
            : `${bottomSpacerClass} ${showLine}`
        }`}
      >
        {showContentAboveImage && (
          <div
            className={
              textPosition
                ? styles[`h1-container-${textPosition}`]
                : styles["h1-container"]
            }
          >
            {heading && (
              <h1
                className={`${styles["koh-article-pages-title"]} ${styles["h1-text"]}`}
                style={{ color: finalHeadingColor }}
              >
                {heading}
              </h1>
            )}
            {(description || description2) && (
              <div
                className={`${description2 && styles["description2"]} ${styles["koh-article-pages-description"]}`}
              >
                {description || description2}
              </div>
            )}
            {label && (
              <div
                className={
                  textPosition
                    ? styles[`cta-container-${textPosition}`]
                    : styles["cta-container"]
                }
              >
                <a
                  className={styles["koh-article-pages-link"]}
                  href={applyLocaleToUrl(url || mediaUrl, locale)}
                >
                  {label}
                </a>
              </div>
            )}
          </div>
        )}
        {(imageUrl || showVideo || assetUrl) && (
          <a
            href={externalUrl || "javascript:void(0)"}
            className={
              externalUrl ? styles["link-wrapper"] : styles["no-link-wrapper"]
            }
          >
            <div className={styles["koh-article-pages-banner"]}>
              {showVideo && (
                <div className={styles["video-wrapper"]}>
                  <iframe
                    className={styles["iframe-video"]}
                    title="Article video content"
                    src={videoUrl}
                    allowFullScreen
                  />
                </div>
              )}
              {(imageUrl || assetUrl) && !showVideo && (
                <img
                  src={imageUrl || assetUrl}
                  alt={altText}
                  className="w-full"
                />
              )}
            </div>
          </a>
        )}
        {showContentBelowImage && (
          <div
            className={
              textPosition
                ? styles[`h1-container-${textPosition}`]
                : styles["h1-container"]
            }
            style={
              finalLineColor
                ? { borderBottom: `1px solid ${finalLineColor}` }
                : {}
            }
          >
            {heading && (
              <h1
                className={`print-font ${styles["koh-article-pages-title"]} !text-[${finalHeadingColor}] ${styles["h1-text"]} 
                text-[26px]`}
                style={{
                  color: finalHeadingColor,
                  fontSize: headingFontSize
                    ? `${headingFontSize || 35}px`
                    : "26px",
                }}
              >
                {heading}
              </h1>
            )}
            {(description || description2) && (
              <p className={styles["koh-article-pages-description"]}>
                {description || description2}
              </p>
            )}
            {label && (
              <div
                className={
                  !showPrintAndShareIcon
                    ? styles["cta-container--no-share"]
                    : styles["cta-container"]
                }
              >
                <a
                  className={styles["koh-article-pages-link"]}
                  href={applyLocaleToUrl(url || mediaUrl, locale)}
                >
                  {label}
                </a>
              </div>
            )}
            {showPrintAndShareIcon && <SocialComponent />}
          </div>
        )}
      </div>
    </Box>
  );
};

ArticleImageIcon.propTypes = {
  articleImageIconProps: PropTypes.shape({
    mod: PropTypes.string,
    image: PropTypes.object,
    heading: PropTypes.string,
    description: PropTypes.string,
    textPosition: PropTypes.string,
    showVideo: PropTypes.bool,
    videoUrl: PropTypes.string,
    showPrintAndShareIcon: PropTypes.bool,
    cta: PropTypes.object,
    showContentAboveImage: PropTypes.bool,
    showContentBelowImage: PropTypes.bool,
  }),
};

export default ArticleImageIcon;
